import { useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import type { RFC3339, UUID } from '@/api/types';

/**
 * v9 D (SPEC_V9 §D, ARCHITECTURE §31): notification channels and
 * preferences, Web Push devices, running late / can't make it. Kept in this
 * feature (not the shared endpoint list) so the area stays self-contained.
 */

export type NotifyChannel = 'email' | 'whatsapp' | 'push';
export type NotifyGroup = 'date_updates' | 'reminders' | 'notes_reveals' | 'profile_views';
export type NotifyPrefs = Record<NotifyChannel, Record<NotifyGroup, boolean>>;

export interface NotificationsView {
  prefs: NotifyPrefs;
  groups: NotifyGroup[];
  channels: NotifyChannel[];
  /** Your own number — never shown to anyone else. */
  whatsapp: { available: boolean; phone: string | null; opted_in_at: RFC3339 | null; opted_out: boolean; opt_out_source: 'stop' | 'webhook' | 'user' | null };
  push: { available: boolean; vapid_public_key: string | null; devices: { endpoint: string; device: string; created_at: RFC3339 }[] };
}

export type PrefsPatch = Partial<Record<NotifyChannel, Partial<Record<NotifyGroup, boolean>>>>;

export interface PushSubscriptionInput {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  device?: string;
}

/** A running-late (`late`, minutes) or can't-make-it (`cant`, reason id) notice. */
export interface LateNotice {
  user_id: UUID;
  kind: 'late' | 'cant';
  minutes: 5 | 10 | 15 | null;
  reason: string | null;
  at: RFC3339;
  /** Running late: the new arrival time. */
  eta: RFC3339 | null;
}

export interface LateView {
  invite_id: UUID;
  starts_at: RFC3339 | null;
  notices: LateNotice[];
  can_late: boolean;
  late_left: number;
  window_from: RFC3339 | null;
  window_to: RFC3339 | null;
  minutes: number[];
  reasons: { id: string; text: string }[];
}

const req = api.request;

export const notifyApi = {
  notifications: () => req<NotificationsView>('/v1/me/notifications'),
  savePrefs: (prefs: PrefsPatch) => req<NotificationsView>('/v1/me/notifications', { method: 'PUT', body: { prefs } }),
  setWhatsApp: (phone: string, optIn: boolean) => req<NotificationsView>('/v1/me/notifications/whatsapp', { method: 'PUT', body: { phone, opt_in: optIn } }),
  removeWhatsApp: () => req<NotificationsView>('/v1/me/notifications/whatsapp', { method: 'DELETE' }),
  subscribePush: (s: PushSubscriptionInput) => req<NotificationsView>('/v1/me/push/subscriptions', { method: 'POST', body: s }),
  unsubscribePush: (endpoint: string) => req<NotificationsView>('/v1/me/push/unsubscribe', { method: 'POST', body: { endpoint } }),
  late: (inviteId: string) => req<LateView>(`/v1/date-invites/${inviteId}/late`),
  runningLate: (inviteId: string, minutes: number) => req<LateView>(`/v1/date-invites/${inviteId}/late`, { method: 'POST', body: { minutes } }),
  cantMakeIt: (inviteId: string, reason: string) => req<LateView>(`/v1/date-invites/${inviteId}/cant-make-it`, { method: 'POST', body: { reason } }),
};

export const notifyKeys = {
  notifications: ['me', 'notifications'] as const,
  late: (inviteId: string) => ['date-invite', inviteId, 'late'] as const,
};

export const useNotifications = (enabled = true) =>
  useQuery({ queryKey: notifyKeys.notifications, queryFn: notifyApi.notifications, enabled, staleTime: 30_000 });

/** Running-late notices for one date (WS `date_invite.late` refreshes it). */
export const useLate = (inviteId: string, enabled = true) =>
  useQuery({ queryKey: notifyKeys.late(inviteId), queryFn: () => notifyApi.late(inviteId), enabled: enabled && !!inviteId, staleTime: 15_000, refetchInterval: 60_000, retry: false });
